import { RateTableType } from "@redotech/redo-conversion-checkout-shipping-common/shipping-rate-table-enums";
import { AustraliaPostServices } from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import {
  ApcServices,
  CanadaPostServices,
  Carriers,
  DhlEcsServices,
  DhlExpressServices,
  EPostGlobalServices,
  FedexServices,
  UpsServices,
  UspsServices,
} from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { isDef } from "@redotech/util/checks";
import {
  type DynamicShippingRateTable,
  type ShippingRateTable,
} from "./shipping-rate-table";

/**
 * Carrier+service combinations that support landed cost calculations via Zonos.
 * This is the canonical source of truth for the UI — derived from the Zonos
 * service level mapping in redo/shipping-shared/src/zonos-service-level-mapping.ts.
 *
 * Only includes carriers that are available in the CarrierAndService schema
 * (the 6 carriers selectable through the checkout experience UI).
 */
export const landedCostSupportedServices: Partial<
  Record<Carriers, ReadonlySet<string>>
> = {
  [Carriers.FEDEX]: new Set([
    FedexServices.FEDEX_INTERNATIONAL_CONNECT_PLUS,
    FedexServices.INTERNATIONAL_ECONOMY,
    FedexServices.INTERNATIONAL_PRIORITY,
    FedexServices.FEDEX_INTERNATIONAL_PRIORITY,
    FedexServices.INTERNATIONAL_PRIORITY_FREIGHT,
  ]),
  [Carriers.UPS]: new Set([
    UpsServices.WorldWideExpress,
    UpsServices.WorldWideExpedited,
    UpsServices.WorldWideSaver,
    UpsServices.UPSStandard,
  ]),
  [Carriers.USPS]: new Set([
    UspsServices.Priority,
    UspsServices.Express,
    UspsServices.ExpressMailInternational,
    UspsServices.PriorityMailInternational,
    UspsServices.FirstClassPackageInternationalService,
  ]),
  [Carriers.AMAZON]: new Set(),
  [Carriers.DHL_EXPRESS]: new Set([DhlExpressServices.ExpressWorldwide]),
  [Carriers.DHL_ECS]: new Set([
    DhlEcsServices.DhlParcelInternationalDirect,
    DhlEcsServices.DhlParcelInternationalDirectPriority,
    DhlEcsServices.DhlParcelInternationalDirectStandard,
    DhlEcsServices.DhlParcelInternationalDirectSMB,
    DhlEcsServices.DhlPacketInternational,
  ]),
  [Carriers.EPOST_GLOBAL]: new Set([
    EPostGlobalServices.PriorityParcelDeliveryConfirmationDDP,
  ]),
  [Carriers.APC]: new Set([
    ApcServices.ParcelConnectExpeditedDDP,
    ApcServices.ParcelConnectPriorityDDPDelcon,
    ApcServices.ParcelConnectPriorityDDP,
  ]),
  [Carriers.AUSTRALIA_POST]: new Set([
    AustraliaPostServices.InternationalStandardPackTrack,
    AustraliaPostServices.IntlStandardPackTrack,
    AustraliaPostServices.InternationalExpressMerchandise,
    AustraliaPostServices.IntlExpressMerch,
    AustraliaPostServices.InternationalEconomyAirmailParcels,
    AustraliaPostServices.InternationalEconomyWSODREGPost,
  ]),
  [Carriers.CANADA_POST]: new Set([
    CanadaPostServices.XpresspostUSA,
    CanadaPostServices.ExpeditedParcelUSA,
    CanadaPostServices.SmallPacketUSAAir,
    CanadaPostServices.TrackedPacketUSA,
    CanadaPostServices.TrackedPacketUSALVM,
  ]),
};

export function isLandedCostSupportedCarrierService(
  carrier: Carriers,
  service: string,
): boolean {
  return landedCostSupportedServices[carrier]?.has(service) ?? false;
}

export function getLandedCostSupportedServicesForCarrier(
  carrier: Carriers,
): ReadonlySet<string> {
  return landedCostSupportedServices[carrier] ?? new Set();
}

export interface LandedCostIncompatibility {
  reason: "timeframe-bucketed" | "unsupported-service" | "missing-flat-service";
  rateTableName: string;
}

export function landedCostIncompatibilityMessage(
  reason: LandedCostIncompatibility["reason"],
): string {
  switch (reason) {
    case "timeframe-bucketed":
      return "Timeframe-bucketed carrier selection is not supported.";
    case "unsupported-service":
      return "One or more selected carrier services are not supported for landed cost calculations.";
    case "missing-flat-service":
      return "Flat rate methods must select a single service for the landed cost quote.";
  }
}

/** Checks rate tables for landed cost incompatibilities. */
export function getLandedCostIncompatibilities(
  rateTables: ShippingRateTable[],
): LandedCostIncompatibility[] {
  const incompatibilities: LandedCostIncompatibility[] = [];

  for (const rateTable of rateTables) {
    if (rateTable.type === RateTableType.FIXED) {
      const carrierService = rateTable.carrierService;
      if (!carrierService) {
        incompatibilities.push({
          reason: "missing-flat-service",
          rateTableName: rateTable.name,
        });
        continue;
      }
      if (
        !isLandedCostSupportedCarrierService(
          carrierService.carrier,
          carrierService.service,
        )
      ) {
        incompatibilities.push({
          reason: "unsupported-service",
          rateTableName: rateTable.name,
        });
      }
      continue;
    }

    if (rateTable.type !== RateTableType.DYNAMIC) {
      continue;
    }

    const dynamicTable = rateTable as DynamicShippingRateTable;

    if (isDef(dynamicTable.serviceLevelBucketedServiceSelection)) {
      incompatibilities.push({
        reason: "timeframe-bucketed",
        rateTableName: rateTable.name,
      });
      continue;
    }

    for (const rate of dynamicTable.rates) {
      if (
        !isLandedCostSupportedCarrierService(
          rate.carrierService.carrier,
          rate.carrierService.service,
        )
      ) {
        incompatibilities.push({
          reason: "unsupported-service",
          rateTableName: rateTable.name,
        });
        break;
      }
    }
  }

  return incompatibilities;
}
